import FileItem from '@/components/Form/Shared/FileItem'
import { Attachment } from '@vnetwork/datacentral-core/domain'
import dayjs, { Dayjs } from 'dayjs'
import {
    displayMoneyLocale,
    formatMoney,
    moneyLocale,
} from '@vnetwork/datacentral-shell/money'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import React from 'react'
import { toast } from '@vnetwork/datacentral-ui/feedback'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
dayjs.extend(isSameOrBefore)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Ho_Chi_Minh')

export const copyToClipboard = (text: string, t: Function) => {
    navigator.clipboard
        .writeText(text)
        .then(() => {
            toast.success(t('copied'))
        })
        .catch(() => {
            toast.error(t('copied'))
        })
}

/** Format file size in bytes to human-readable (KB, MB, GB, ...) using 1024-based units */
export const formatFileSize = (bytes: number): string => {
    if (bytes <= 0) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    const index = Math.min(i, units.length - 1)
    return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 2)} ${units[index]}`
}

export const isAttachment = (file: File | Attachment): boolean => {
    return (
        typeof file === 'object' &&
        file !== null &&
        'attachment_file_name' in file &&
        'attachment_file_url' in file
    )
}
export const renderFileItem = (
    file: File | Attachment,
    onDelete?: (file: File | Attachment) => void,
    presignedPreview?: string
) => {
    if (!file) return null
    const isFile = file instanceof File

    if (isFile) {
        return React.createElement(FileItem, {
            key: file.name,
            name: file.name,
            preview: (file as any)?.preview || undefined,
            size: file.size,
            type: file.type,
            onDelete: () => onDelete?.(file),
        })
    }

    if (isAttachment(file)) {
        return React.createElement(FileItem, {
            key: file.attachment_file_name,
            name: file.attachment_file_name,
            preview:
                presignedPreview || getFilePreview(file, 'attachment_file_url'),
            thumbnail: presignedPreview
                ? undefined
                : getFilePreview(file, 'thumbnail'),
            size: file.attachment_file_size,
            type: file.attachment_file_type,
            onDelete: () => onDelete?.(file),
        })
    }

    return null
}

/**
 * Format currency
 * @param amount - The amount to format
 * @param currency - The currency to format
 * @param locale - The locale to format
 * @returns The formatted currency
 */

export const formatCurrency = (
    amount: number,
    currency = 'VND',
    locale: string = displayMoneyLocale()
) => {
    if (isNaN(amount)) {
        return amount
    }
    // Fallback to VND when currency is null/empty
    if (!currency) currency = 'VND'
    if (currency === 'VND') {
        // Nếu số tiền < 1, hiển thị với số thập phân
        const fractionDigits = amount < 1 && amount > 0 ? 2 : 0
        return formatMoney(amount, {
            currency,
            locale: moneyLocale(locale),
            minDecimals: fractionDigits,
            maxDecimals: fractionDigits,
        })
    }

    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
    }).format(amount)
}

export const formatNumber = (amount: number) => {
    if (isNaN(amount)) {
        return amount
    }

    return Intl.NumberFormat('en-US').format(amount)
}

export const toDate = (data: any) => {
    if (!data) return null
    return typeof data?.format === 'function'
        ? data?.format('YYYY-MM-DD')
        : dayjs(data).format('YYYY-MM-DD')
}

export const formatDate = (data: string | null) => {
    if (!data) return '---'
    return dayjs(data).format('DD/MM/YYYY')
}

/**
 * Format date with formatter
 * @param data - The date to format
 * @param formatter - The formatter to use
 * @param options - Additional options
 * @param options.fromUTC - If true, treat input as UTC and convert to local timezone (GMT+7)
 * @returns The formatted date
 */
export const formatDateWithFormatter = (
    data: string | Dayjs,
    formatter: string = 'DD/MM/YYYY',
    options?: { fromUTC?: boolean }
) => {
    if (!data) return '---'

    if (options?.fromUTC) {
        // Parse as UTC and convert to GMT+7 (Vietnam timezone)
        return dayjs.utc(data).utcOffset(7).format(formatter)
    }

    return dayjs(data).format(formatter)
}
export const omit = <T extends Record<string, any>, K extends keyof T>(
    obj: T,
    keys: K | K[]
): Omit<T, K> => {
    const newObj = { ...obj }
    ;(Array.isArray(keys) ? keys : [keys]).forEach((key) => delete newObj[key])
    return newObj
}

export const pick = <T extends object, K extends keyof T>(
    obj: T,
    keys: K[]
): Pick<T, K> => {
    return keys.reduce(
        (result, key) => {
            if (key in obj) {
                ;(result as any)[key] = obj[key] // Ép kiểu tránh lỗi
            }
            return result
        },
        {} as Pick<T, K>
    )
}

export const uniqBy = <T, K extends keyof T>(array: T[], key: K): T[] => {
    const seen = new Set<T[K]>()
    return array?.filter((item) => {
        const val = item?.[key]
        if (seen.has(val as T[K])) return false
        seen.add(val as T[K])
        return true
    })
}

export const formatNumberUsage = (usage: number) => {
    const UNLIMITED = 999_999_999
    if (usage >= UNLIMITED) {
        return 'UNLIMITED'
    }
    if (isNaN(usage)) {
        return ''
    }

    return new Intl.NumberFormat('en-US', {
        style: 'decimal',
        useGrouping: true,
    }).format(usage)
}

type ScopePGFormat =
    | 'YYYY-MM-DD'
    | 'YYYY-MM-DDTHH:mm:ss'
    | 'YYYY-MM-DDTHH:mm:ssZ'
export const formatPGDate = (date?: string | Dayjs, scope?: ScopePGFormat) => {
    if (!date) return null
    return dayjs(date).format(scope || 'YYYY-MM-DD')
}

export function max<T>(array: T[] | undefined | null): T | undefined {
    if (!Array.isArray(array) || array.length === 0) return undefined

    return array.reduce((maxItem, current) =>
        current > maxItem ? current : maxItem
    )
}
export function formatBandwidthUnit(
    value: number,
    digit: number = 2,
    maxResolve: string = 'Xbps',
    unitOutput?: boolean
): string {
    const unit = [
        'bps',
        'Kbps',
        'Mbps',
        'Gbps',
        'Tbps',
        'Pbps',
        'Ebps',
        'Zbps',
        'Ybps',
        'Bbps',
        'Nbps',
        'Dbps',
        'Cbps',
        'Xbps',
    ]

    let i = 0
    const maxIndex = unit.indexOf(maxResolve)
    while (value > 1000 && i < maxIndex) {
        value = value / 1000
        ++i
    }

    const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: digit,
        maximumFractionDigits: digit,
    })

    const formattedValue = formatter.format(value)
    const finalUnit = unitOutput ? unit[i]?.replace('ps', '') : unit[i]

    return `${formattedValue} ${finalUnit}`
}

/**
 * Format a data VOLUME (bytes) to human-readable decimal units (B/KB/MB/GB/TB...).
 * Use this for volume/traffic metrics — NOT formatBandwidthUnit, which emits bit-rate
 * units (bps) and is only correct for throughput. Decimal (1000-based) to match the
 * CDN billing convention (1 GB = 1e9 bytes).
 */
export function formatVolumeUnit(value: number, digit: number = 2): string {
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB']

    let v = value || 0
    let i = 0
    while (v >= 1000 && i < units.length - 1) {
        v = v / 1000
        ++i
    }

    const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: digit,
        maximumFractionDigits: digit,
    })

    return `${formatter.format(v)} ${units[i]}`
}

/**
 * Format a data VOLUME (bytes) as a GB figure (decimal, 1 GB = 1e9 bytes) with thousands
 * separators, rounded to the nearest whole GB (>=0.5 up, <0.5 down via Intl halfExpand).
 * Use alongside formatVolumeUnit when the auto-scaled unit (PB/EB) rounds away large
 * absolute differences — e.g. CDN reconcile where 1.05 PB could hide tens of TB. Matches
 * the vendor CSV "Volume(GB)" convention.
 */
export function formatVolumeGB(value: number, digit: number = 0): string {
    const gb = (value || 0) / 1e9
    const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: digit,
        maximumFractionDigits: digit,
    })
    return `${formatter.format(gb)} GB`
}

export function formatBinaryStorageUnit(
    value: number,
    digit: number = 2,
    maxResolve: string = 'XiB',
    unitOutput?: boolean
): string {
    const unit = [
        'B',
        'KiB',
        'MiB',
        'GiB',
        'TiB',
        'PiB',
        'EiB',
        'ZiB',
        'YiB',
        'BiB',
        'NiB',
        'DiB',
        'CiB',
        'XiB',
    ]

    let i = 0
    const maxIndex = unit.indexOf(maxResolve)
    while (value > 1024 && i < maxIndex) {
        value = value / 1024
        ++i
    }

    const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: digit,
        maximumFractionDigits: digit,
    })

    const formattedValue = formatter.format(value)
    const finalUnit = unitOutput
        ? unit[i]?.replace('iB', 'B').replace('B', '')
        : unit[i]

    return `${formattedValue} ${finalUnit}`
}

export const buildYearOptions = ({
    start = 2013,
    end,
}: {
    start: number
    end?: number
}): { value: string; label: string }[] => {
    if (!end) {
        end = new Date().getFullYear()
    }

    const options = []
    for (let year = start; year <= end; year++) {
        options.push({
            value: year.toString(),
            label: year.toString(),
        })
    }

    return options
}

export function formatNumeral(
    value: number,
    digit: number = 2,
    locale: string = 'en-US'
): string {
    if (!Number.isFinite(value)) return ''

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: digit,
        maximumFractionDigits: digit,
    }).format(value)
}

// Moved to @vnetwork/datacentral-shell (#59) so an extracted module can use it
// without importing this file; re-exported so existing imports are unchanged.
export { deepEqual } from '@vnetwork/datacentral-shell/deep-equal'

export function toDayjs(day: any) {
    return typeof day?.format !== 'function' ? dayjs(day) : day
}

export const downloadFile = async (fileUrl: string, fileName: string) => {
    try {
        const response = await fetch(fileUrl)
        const blob = await response.blob()

        // Create a temporary anchor element
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName // Suggests file name for saving

        // Append to document and trigger click
        document.body.appendChild(link)
        link.click()

        // Cleanup
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
    } catch (error) {
        console.error('Download failed:', error)
    }
}

// check if date is before today
// @param date: string | Dayjs
// @return boolean
export const isBeforeToday = (date: string | Dayjs) => {
    const today = dayjs()
    // check diff day between date and today
    const diffDay = dayjs(date).diff(today, 'day')
    return diffDay < 0
}

// convert 2025-07-06T00:00:00Z to 2025-07-06
export const convertDateToLocal = (date: string) => {
    return dayjs(date).format('YYYY-MM-DD')
}

const getFilePreview = (
    file: File | Attachment,
    scope: 'thumbnail' | 'attachment_file_url' | 'preview' = 'preview'
): string => {
    if (file instanceof File) {
        return (file as any)?.preview
    }

    return (
        import.meta.env.APP_STORAGE + '/' + (file as Attachment)?.[scope] ||
        (file as Attachment)?.attachment_file_url
    )
}

export const startOf = (
    date: string | Dayjs,
    granularity: 'day' | 'week' | 'month' | 'year'
) => {
    return dayjs(date).startOf(granularity).format('YYYY-MM-DD')
}

export const endOf = (
    date: string | Dayjs,
    granularity: 'day' | 'week' | 'month' | 'year'
) => {
    return dayjs(date).endOf(granularity).format('YYYY-MM-DD')
}

export const formatFromUTC = (
    dateStr?: string | null,
    format: string = 'DD/MM/YYYY HH:mm'
) => (dateStr ? dayjs.utc(dateStr).tz().format(format) : '')

export function formatRequest(
    value: number,
    digit: number = 2,
    maxResolve: string = 'Xbps',
    unitOutput?: boolean
): string {
    // request, K Request, M(Milion) Request
    const unit = ['Request', 'K Request', 'M Request']

    let i = 0
    const maxIndex = unit.indexOf(maxResolve)
    while (value > 1000 && i < maxIndex) {
        value = value / 1000
        ++i
    }

    const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: digit,
        maximumFractionDigits: digit,
    })

    const formattedValue = formatter.format(value)
    const finalUnit = unitOutput ? unit[i]?.replace('Request', '') : unit[i]

    return `${formattedValue} ${finalUnit}`
}

/**
 * Abbreviate long product names to acronyms
 * e.g., "CONTENT DELIVERY NETWORK" -> "CDN"
 *       "CLOUD SERVICE" -> "Cloud Service" (if short enough)
 */
export const abbreviateProductName = (name: string, maxLen = 12): string => {
    if (!name) return 'Unknown'

    // If already short enough, capitalize properly
    if (name.length <= maxLen) {
        return name
            .toLowerCase()
            .split(' ')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ')
    }

    // Create acronym from words
    const words = name.trim().split(/\s+/)
    if (words.length >= 2) {
        const acronym = words.map((w) => w.charAt(0).toUpperCase()).join('')
        return acronym
    }

    // Single long word - truncate
    return name.slice(0, maxLen) + '...'
}

/**
 * Abbreviate Vietnamese name: "Huỳnh Long Khả Di" → "HLK.Di", "Nguyen_Quoc_Chung" → "NQ.Chung"
 * Keeps last name part, abbreviates the rest to initials
 */
export const abbreviateName = (name: string): string => {
    const capitalize = (s: string) =>
        s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
    const parts = name.trim().replace(/_/g, ' ').split(/\s+/)
    if (parts.length <= 1) return capitalize(name)
    const initials = parts
        .slice(0, -1)
        .map((p) => p.charAt(0).toUpperCase())
        .join('')
    return `${initials}.${capitalize(parts[parts.length - 1])}`
}
